import { useEffect, useRef, useState } from 'react';
import { EducateNavHeader } from '@/components/brand/EducateNavHeader';
import { WelcomeScreen } from '@/pages/WelcomeScreen';
import { CompleteScreen } from '@/pages/CompleteScreen';
import { ErrorScreen, LoadingScreen } from '@/components/StatusScreens';
import { ModuleRouterV2 } from '@/pages/playerV2/ModuleRouterV2';
import { useEducateModule } from '@/hooks/useEducateModule';
import { EDUCATE_TYPE_LABEL } from '@/utils/units';
import { ReadOutButton } from '@/components/ReadOut/ReadOutButton';
import { resolveUiConfig } from '@/pages/playerV2/host/uiConfig';
import type { ConceptAppearance, ScreenConfig, SlotStyle } from '@knolskape/educate-components';

const DESIGN_WIDTH = 1280;

function ScaledModuleCanvas({ children }: { children: React.ReactNode }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [availWidth, setAvailWidth] = useState(0);
  const [availHeight, setAvailHeight] = useState(0);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setAvailWidth(width);
      setAvailHeight(height);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const isWide = availWidth >= DESIGN_WIDTH;
  const scale = availWidth > 0 && !isWide ? availWidth / DESIGN_WIDTH : 1;
  const innerWidth = isWide ? availWidth : DESIGN_WIDTH;

  return (
    <div
      ref={wrapperRef}
      style={{
        width: '100%',
        flex: 1,
        overflow: 'hidden',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div
        style={{
          width: innerWidth,
          ...(isWide
            ? { flex: '1 1 0%', minHeight: 0 }
            : {
                height: availHeight > 0 ? availHeight / scale : undefined,
                transformOrigin: 'top left',
                transform: `scale(${scale})`
              }),
          visibility: availWidth > 0 ? 'visible' : 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function App() {
  const module = useEducateModule();
  const { phase, error, storyline, progress, units, start, restart } = module;

  const resolvedConfig = storyline ? resolveUiConfig(storyline) : null;
  const appearance = (resolvedConfig as { appearance?: ConceptAppearance } | null)?.appearance;
  const readout = (
    resolvedConfig as { readout?: { enabled?: boolean; lang?: string; gender?: string } } | null
  )?.readout;
  const welcomeOverrides = (resolvedConfig as { welcomeScreen?: ScreenConfig } | null)
    ?.welcomeScreen?.overrides as Record<string, SlotStyle> | undefined;
  const completeOverrides = (resolvedConfig as { completeScreen?: ScreenConfig } | null)
    ?.completeScreen?.overrides as Record<string, SlotStyle> | undefined;
  const pageBg = appearance?.theme?.background ?? '#ffffff';
  const showReadout = !!storyline && readout?.enabled === true;

  if (phase === 'loading') return <Shell>{<LoadingScreen />}</Shell>;

  if (phase === 'error' || !storyline) {
    return (
      <Shell>
        <ErrorScreen message={error ?? 'This module is unavailable.'} />
      </Shell>
    );
  }

  if (phase === 'module') {
    return (
      <div
        style={{
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <ScaledModuleCanvas>
          <ModuleRouterV2 module={module} />
        </ScaledModuleCanvas>
        {showReadout && (
          <ReadOutButton
            lang={readout?.lang ?? 'en-US'}
            gender={(readout?.gender as 'any' | 'female' | 'male') ?? 'any'}
            pageBg={pageBg}
          />
        )}
      </div>
    );
  }

  return (
    <Shell
      title={storyline.uiContent.title}
      subtitle={EDUCATE_TYPE_LABEL[storyline.educateType]}
      appearance={appearance}
    >
      {phase === 'welcome' && (
        <WelcomeScreen
          storyline={storyline}
          progress={progress}
          unitCount={units.length}
          onStart={start}
          screenOverrides={welcomeOverrides}
        />
      )}

      {phase === 'complete' && (
        <CompleteScreen
          storyline={storyline}
          onRestart={restart}
          screenOverrides={completeOverrides}
        />
      )}
    </Shell>
  );
}

function Shell({
  title = '4E Educate',
  subtitle = 'Educate',
  children,
  appearance
}: {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  appearance?: ConceptAppearance;
}) {
  return (
    <EducateNavHeader eyebrow={subtitle} title={title} appearance={appearance}>
      <main className="w-full px-[18%] py-12">{children}</main>
    </EducateNavHeader>
  );
}
